/**
 * @module UploadReceiptModal
 * @purpose Modal for uploading a collection receipt package with progress tracking
 * @depends useUploadReceiptModal, upload-receipt-modal.parts, useTranslation
 * @dependedOnBy CollectionRequestModals, ProcessableCollectionsSection
 *
 * @rules
 * - Performance: XHR upload with streaming progress events
 * - State: delegated entirely to useUploadReceiptModal hook
 * - Errors: TARGET_ALREADY_RECEIVED mapped to alreadyExists step; evidence_not_received and
 *   network errors to error step with retry; submit is also proactively gated (chain-of-custody
 *   not received) via FormActions' `gated` prop
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { CollectionReceipt } from '../../types/collection-receipts'
import { getUploadGateState } from '../collections/UploadGateStatus'
import {
  ModalHeader,
  ProgressBar,
  InlineError,
  SuccessMessage,
  FormActions,
} from './upload-receipt-modal/upload-receipt-modal.parts'
import { PickStateForm } from './upload-receipt-modal/upload-receipt-modal.pick-form'
import {
  useUploadReceiptModal,
  type UploadReceiptModalState,
} from './upload-receipt-modal/use-upload-receipt-modal'
import {
  ProbeErrorState,
  ProbingState,
  StagedTransportActions,
} from './upload-receipt-modal/upload-receipt-modal.staged-view'

interface UploadReceiptModalProps {
  isOpen: boolean
  onClose: () => void
  caseId: string
  requestId: string
  onSuccess: (receipt: CollectionReceipt) => void
}

interface SuccessCloseButtonProps {
  onClose: () => void
  t: TFunction<'cases'>
}

function SuccessCloseButton({ onClose, t }: SuccessCloseButtonProps): ReactElement {
  return (
    <div className="flex justify-end pt-2">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-200 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {t('detail.collections.uploadReceipt.cancel')}
      </button>
    </div>
  )
}

interface BodyProps {
  state: UploadReceiptModalState
  caseId: string
  isSuccess: boolean
  isUploading: boolean
  onClose: () => void
  t: TFunction<'cases'>
}

interface DuplicateHashWarningProps {
  message: string | null
  uploading: boolean
  onConfirm: () => void
  onCancel: () => void
  t: TFunction<'cases'>
}

/**
 * Duplicate-package warning with an explicit opt-in to proceed.
 *
 * @behaviour Only rendered when the server returned DUPLICATE_HASH_OVERRIDABLE,
 *   i.e. the operator enabled the override. A plain DUPLICATE_HASH stays a hard
 *   stop and never reaches this step. Confirming re-sends the same file with
 *   `allowDuplicate`, and the duplicate is recorded as its own receipt rather
 *   than replacing the original.
 */
function DuplicateHashWarning({
  message,
  uploading,
  onConfirm,
  onCancel,
  t,
}: DuplicateHashWarningProps): ReactElement {
  return (
    <div
      role="alert"
      className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40"
    >
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
        {t('detail.collections.uploadReceipt.duplicateHashTitle')}
      </p>
      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
        {message ?? t('detail.collections.uploadReceipt.duplicateHashWarning')}
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={uploading}
          className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {t('detail.collections.uploadReceipt.duplicateHashConfirm')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-800 disabled:opacity-50 dark:border-amber-700 dark:text-amber-300"
        >
          {t('detail.collections.uploadReceipt.duplicateHashCancel')}
        </button>
      </div>
    </div>
  )
}

/** Error and warning banners for the upload modal, by step. */
function ModalAlerts({ state, t }: { state: BodyProps['state']; t: TFunction<'cases'> }): ReactElement {
  if (state.step === 'duplicateHash') {
    return (
      <DuplicateHashWarning
        message={state.error}
        uploading={state.uploading}
        onConfirm={() => { void state.handleConfirmDuplicate() }}
        onCancel={state.handleRetry}
        t={t}
      />
    )
  }
  if ((state.step === 'error' || state.step === 'alreadyExists') && state.error !== null) {
    return <InlineError message={state.error} step={state.step} onRetry={state.handleRetry} t={t} />
  }
  return <></>
}

/**
 * The staged-transport steering sub-flow (spec FR-staged): once the probe
 * resolves to `staged`, this REPLACES the normal upload submit action --
 * the HTTP path below stays byte-identical for `transport === 'http_upload'`
 * (and while the probe has not resolved yet).
 */
function TransportAwareActions({ state, caseId, onClose, t }: BodyProps): ReactElement {
  if (state.transportProbe?.transport === 'staged') {
    return <StagedTransportActions state={state} caseId={caseId} t={t} />
  }
  const gated = state.target !== null && getUploadGateState(state.target) === 'gated'
  return <FormActions uploading={state.uploading} gated={gated} onClose={onClose} t={t} />
}

function TransportProbeBanner({ state, t }: { state: UploadReceiptModalState; t: TFunction<'cases'> }): ReactElement {
  if (state.probing) return <ProbingState t={t} />
  if (state.probeError) return <ProbeErrorState message={state.probeError} onRetry={state.retryProbe} t={t} />
  return <></>
}

function ModalBody({ state, caseId, isSuccess, isUploading, onClose, t }: BodyProps): ReactElement {
  return (
    <>
      <ModalAlerts state={state} t={t} />
      {isSuccess && <SuccessMessage t={t} />}
      {isUploading && <ProgressBar percent={state.progress} t={t} />}
      {!isSuccess && <PickStateForm state={state} t={t} />}
      {!isSuccess && <TransportProbeBanner state={state} t={t} />}
      {!isSuccess && (
        <TransportAwareActions state={state} caseId={caseId} isSuccess={isSuccess} isUploading={isUploading} onClose={onClose} t={t} />
      )}
      {isSuccess && <SuccessCloseButton onClose={onClose} t={t} />}
    </>
  )
}

/**
 * Modal for registering a returned Velociraptor collection package.
 *
 * @behaviour Shows a target picker (filtered to in-scope statuses plus received-but-not-uploaded
 *            targets), zip + optional log file pickers, streams upload progress, and handles
 *            already-received / evidence-not-received / server-error states distinctly.
 * @errors Client-side validation for non-zip and oversized files; TARGET_ALREADY_RECEIVED shows
 *         alreadyExists state; evidence_not_received shows the chain-of-custody warning.
 */
export function UploadReceiptModal(props: UploadReceiptModalProps): ReactElement | null {
  const { isOpen, onClose, caseId, requestId, onSuccess } = props
  const { t } = useTranslation('cases')
  const state = useUploadReceiptModal({ caseId, requestId, onSuccess, t })

  if (!isOpen) return null

  const isUploading = state.step === 'uploading'
  const isSuccess = state.step === 'success'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-slate-800">
        <ModalHeader onClose={onClose} t={t} />
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void state.handleSubmit()
          }}
          className="space-y-4 p-4"
        >
          <ModalBody
            state={state}
            caseId={caseId}
            isSuccess={isSuccess}
            isUploading={isUploading}
            onClose={onClose}
            t={t}
          />
        </form>
      </div>
    </div>
  )
}
